import {
  RocketIcon,
  ArrowUpRightIcon,
  CommentIcon,
  ArrowSwitchIcon,
  HeartIcon,
  HeartFillIcon,
  CommentDiscussionIcon,
} from '@primer/octicons-react'
import {ActionMenu, ActionList, ConfirmationDialog, FormControl, Textarea} from '@primer/react'
import {clsx} from 'clsx'
import {useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {FeedbackDialog, type FeedbackDialogRef} from '@github-ui/feedback-dialog'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useToggleFeaturePreview} from '../../../hooks/use-toggle-feature-preview'
import {useLayoutRouteData} from '../../../page-data/loaders/use-layout-route-data'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'
import {OVERVIEW_FEEDBACK_SUBJECT} from '../../../helpers/constants'
import {AlphaLabel} from '@github-ui/lifecycle-labels/alpha'
import {sendCustomMetric, isStaff} from '@github-ui/stats'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import styles from './OverviewFeedback.module.css'
import {isBoolean} from '../../../utils/validators'
import {useMutation} from '@github-ui/react-query'
import {MutationFailureBanner} from '../../FailureBanner'
import {useCurrentPathParams} from '../data/use-comments-routing'
import {pullRequestsOverviewIndexRoute} from '../../../routes/overview-route'
import {Link} from '@github-ui/react-router'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'

const LOVED_STORAGE_KEY = 'pull-requests.overview.loved-feature'
const REASON_MAX_LENGTH = 2_000

export function OverviewFeedback() {
  const {urls} = useLayoutRouteData()
  const toggleFeature = useToggleFeaturePreview('prs_overview_react')
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const [loved, setLoved] = useLocalStorage(LOVED_STORAGE_KEY, false, {validator: isBoolean})
  const [animateLove, setAnimateLove] = useState(false)
  const [showOptOutDialog, setShowOptOutDialog] = useState(false)
  const [reason, setReason] = useState('')
  const {isBrowser} = useRuntimeEnvironment()
  const staff = isBrowser && isStaff()
  const reasonTooLong = reason.length > REASON_MAX_LENGTH
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const reasonInputRef = useRef<HTMLTextAreaElement>(null)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const feedbackEndpoint = usePageDataUrl(PageData.submitFeatureFeedback)

  const isPublicPreview = isFeatureEnabled('prs_overview_react_public_preview')

  const pathParams = useCurrentPathParams()

  const disableFeatureMutation = useMutation({
    mutationFn: () => toggleFeature({enabled: false, reason}),
    onSuccess: () => {
      sendPullRequestAnalyticsEvent('overview.feature_toggle', 'OVERVIEW_FEATURE_TOGGLE', {enabled: false})
      window.location.href = `${urls.conversation}?overview=false`
    },
  })

  const handleOptOutDialogClose = (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
    if (disableFeatureMutation.isPending) return

    if (gesture !== 'confirm') {
      setShowOptOutDialog(false)
      disableFeatureMutation.reset()
      setTimeout(() => menuTriggerRef.current?.focus(), 0)
      return
    }

    if (reasonTooLong) {
      announce(`Feedback is too long. Use ${REASON_MAX_LENGTH} characters or fewer.`, {assertive: true})
      return
    }

    disableFeatureMutation.mutate()
  }

  const handleLove = () => {
    const nextLoved = !loved
    setLoved(nextLoved)
    setAnimateLove(nextLoved)

    if (nextLoved) {
      sendCustomMetric({name: 'PULL_REQUESTS_OVERVIEW_LOVE_THIS_FEATURE', value: 1})
      sendPullRequestAnalyticsEvent('overview.love_this_feature', 'OVERVIEW_LOVE_THIS_FEATURE')
    } else {
      sendCustomMetric({name: 'PULL_REQUESTS_OVERVIEW_UNLOVE_THIS_FEATURE', value: 1})
      sendPullRequestAnalyticsEvent('overview.unlove_this_feature', 'OVERVIEW_UNLOVE_THIS_FEATURE')
    }
  }

  return (
    <>
      <ActionMenu anchorRef={menuTriggerRef}>
        <ActionMenu.Button
          ref={menuTriggerRef}
          variant="invisible"
          size="small"
          onClick={() => setAnimateLove(false)}
          aria-label={
            isPublicPreview ? 'Preview: Overview preview options' : 'Private preview: Overview preview options'
          }
        >
          {isPublicPreview ? <BetaLabel>Preview</BetaLabel> : <AlphaLabel>Private preview</AlphaLabel>}
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.LinkItem href="https://gh.io/pr-overview" target="_blank" rel="noopener noreferrer">
              <ActionList.LeadingVisual>
                <RocketIcon />
              </ActionList.LeadingVisual>
              What&apos;s new
              <ActionList.TrailingVisual>
                <ArrowUpRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.LinkItem>
            {staff ? (
              <ActionList.LinkItem href="https://gh.io/pr-overview-feedback" target="_blank" rel="noopener noreferrer">
                <ActionList.LeadingVisual>
                  <CommentIcon />
                </ActionList.LeadingVisual>
                Give feedback
                <ActionList.TrailingVisual>
                  <ArrowUpRightIcon />
                </ActionList.TrailingVisual>
              </ActionList.LinkItem>
            ) : (
              <ActionList.Item onSelect={() => feedbackRef.current?.openDialog()}>
                <ActionList.LeadingVisual>
                  <CommentIcon />
                </ActionList.LeadingVisual>
                Give feedback
              </ActionList.Item>
            )}
            <ActionList.Item
              role="menuitemcheckbox"
              aria-checked={loved}
              onSelect={event => {
                event.preventDefault()
                handleLove()
              }}
            >
              <ActionList.LeadingVisual
                className={clsx(loved && styles.lovedHeart, animateLove && styles.loveAnimation)}
                onAnimationEnd={() => setAnimateLove(false)}
              >
                {loved ? <HeartFillIcon /> : <HeartIcon />}
              </ActionList.LeadingVisual>
              <span>{loved ? 'Loved!' : 'Love this feature'}</span>
            </ActionList.Item>
            <ActionList.Divider />
            <ActionList.LinkItem
              as={Link}
              reloadDocument
              data-turbo="false"
              to={{
                pathname: pullRequestsOverviewIndexRoute.generatePath(pathParams),
                search: new URLSearchParams({overview: 'false'}).toString(),
              }}
              onClick={() =>
                sendPullRequestAnalyticsEvent('overview.temporary_feature_toggle', 'OVERVIEW_TEMPORARY_FEATURE_TOGGLE')
              }
            >
              <ActionList.LeadingVisual>
                <CommentDiscussionIcon />
              </ActionList.LeadingVisual>
              View Conversation page
              <ActionList.Description variant="block">Temporarily return to the classic view</ActionList.Description>
            </ActionList.LinkItem>
            <ActionList.Item onSelect={() => setShowOptOutDialog(true)}>
              <ActionList.LeadingVisual>
                <ArrowSwitchIcon />
              </ActionList.LeadingVisual>
              Turn off Overview preview
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {showOptOutDialog && (
        <ConfirmationDialog
          title="Turn off Overview preview?"
          confirmButtonContent="Turn off Overview preview"
          confirmButtonType="danger"
          confirmButtonLoading={disableFeatureMutation.isPending}
          width="large"
          onClose={handleOptOutDialogClose}
        >
          <MutationFailureBanner
            mutation={disableFeatureMutation}
            title="Failed to turn off Overview preview"
            returnFocusRef={reasonInputRef}
            className="mb-3"
          />
          <FormControl id="overview-opt-out-reason">
            <FormControl.Label>Why are you turning off Overview preview?</FormControl.Label>
            <FormControl.Caption>Optional</FormControl.Caption>
            <Textarea
              block
              rows={3}
              characterLimit={REASON_MAX_LENGTH}
              value={reason}
              resize="vertical"
              onChange={event => setReason(event.currentTarget.value)}
              ref={reasonInputRef}
            />
            {reasonTooLong && (
              <FormControl.Validation variant="error">
                Feedback is too long. Use {REASON_MAX_LENGTH} characters or fewer.
              </FormControl.Validation>
            )}
          </FormControl>
        </ConfirmationDialog>
      )}
      {!staff && (
        <FeedbackDialog
          ref={feedbackRef}
          subject={OVERVIEW_FEEDBACK_SUBJECT}
          hideButton
          endpoint={feedbackEndpoint}
          returnFocusRef={menuTriggerRef}
        />
      )}
    </>
  )
}
